<script setup lang="ts">
import { computed, onMounted, reactive, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import {
  DxDataGrid,
  DxColumn,
  DxScrolling,
} from 'devextreme-vue/data-grid';
import { DxButton } from 'devextreme-vue/button';
import { DxDropDownButton } from 'devextreme-vue/drop-down-button';
import { DxSelectBox } from 'devextreme-vue/select-box';
import { DxTextBox } from 'devextreme-vue/text-box';
import { DxTabPanel, DxItem } from 'devextreme-vue/tab-panel';
import { BottoPagePanel, getAggregatorsLookup, LookupBox, showError, showSuccess } from '@botto/core';
import { linksApi, type BreakdownRow, type ClickEvent, type StatsSummary, type TrackerLink } from '../../api';
import ClickLogGrid from '../../components/ClickLogGrid.vue';
import DateRangePicker from '../../components/DateRangePicker.vue';
import DimensionFilterFields from '../../components/DimensionFilterFields.vue';
import StatusBadge from '../../components/StatusBadge.vue';
import {
  copyText,
  defaultRangeDays,
  formatBotRate,
  formatDateTime,
  formatNumber,
  toApiDate,
  trackingUrl,
} from '../../utils/format';
import { applyDimensionFilters, emptyDimensionFilters } from '../../utils/dimensionFilters';
import { trackerGridProps } from '../../utils/gridUi';

const props = defineProps<{
  id: string | number;
}>();

const router = useRouter();
const linkId = computed(() => Number(props.id));
const deleting = ref(false);
const tabIndex = ref(0);

const link = ref<TrackerLink | null>(null);
const initRange = defaultRangeDays(7);
const rangeFrom = ref<Date | null>(initRange.from);
const rangeTo = ref<Date | null>(initRange.to);

const summary = ref<StatsSummary | null>(null);
const loadingSummary = ref(false);

const groupOptions = [
  'country',
  'city',
  'device_type',
  'os',
  'browser',
  'language',
  'outcome',
  'day',
  'hour',
  'referer',
  'asn',
];
const groupBy = ref('country');
const breakdownFilters = reactive(emptyDimensionFilters());
const breakdown = ref<BreakdownRow[]>([]);
const loadingBreakdown = ref(false);

const clicks = ref<ClickEvent[]>([]);
const clicksTotal = ref(0);
const page = ref(1);
const pageSize = 50;
const loadingClicks = ref(false);
const exportingCsv = ref(false);
type ExportId = 'csv' | 'csv-full' | 'excel';
const exportItems: { id: ExportId; text: string; icon?: string }[] = [
  { id: 'csv', text: 'CSV' },
  { id: 'csv-full', text: 'CSV full' },
  { id: 'excel', text: 'Excel', icon: 'xlsxfile' },
];
const selectedExportId = ref<ExportId>('csv');
const exportButtonText = computed(() => {
  if (exportingCsv.value) return '…';
  return exportItems.find((i) => i.id === selectedExportId.value)?.text ?? 'CSV';
});
const filters = reactive({
  outcome: '',
  is_bot: '' as '' | '0' | '1',
  country: '',
  language: '',
  device_type: '',
  correlation_id: '',
  aggregator_id: null as number | null,
});

const aggregatorsLookup = getAggregatorsLookup();

const outcomeItems = [
  { value: '', text: 'Все' },
  { value: 'click', text: 'click' },
  { value: 'redirect', text: 'redirect' },
  { value: 'landing', text: 'landing' },
  { value: 'challenge', text: 'challenge' },
  { value: 'expired', text: 'expired' },
  { value: 'paused', text: 'paused' },
];

const botItems = [
  { value: '', text: 'Все' },
  { value: '0', text: 'Human' },
  { value: '1', text: 'Bot' },
];

const displayUrl = computed(() => {
  if (!link.value) return '';
  return link.value.tracking_url || trackingUrl(link.value.domain_name, link.value.code);
});

const linkTitle = computed(() => {
  if (!link.value) return 'Ссылка';
  const name = link.value.name?.trim();
  return name || link.value.code || 'Ссылка';
});

const summaryCards = computed(() => {
  const s = summary.value;
  if (!s) return [];
  return [
    { label: 'Клики', value: formatNumber(s.clicks) },
    { label: 'Uniques', value: formatNumber(s.unique_clicks) },
    { label: 'Bots', value: formatNumber(s.bots) },
    { label: 'Bot rate', value: formatBotRate(s.bot_rate) },
    { label: 'Redirects', value: formatNumber(s.redirects) },
    { label: 'Landings', value: formatNumber(s.landings) },
    { label: 'Challenges', value: formatNumber(s.challenges) },
    { label: 'Expired', value: formatNumber(s.expired) },
  ];
});

function rangeParams() {
  return {
    from: toApiDate(rangeFrom.value),
    to: toApiDate(rangeTo.value),
  };
}

function breakdownParams() {
  return applyDimensionFilters(
    { ...rangeParams(), group_by: groupBy.value },
    breakdownFilters,
  );
}

async function loadLink() {
  link.value = await linksApi.getLink(linkId.value);
}

async function loadSummary() {
  loadingSummary.value = true;
  try {
    summary.value = await linksApi.linkSummary(linkId.value, rangeParams());
  } finally {
    loadingSummary.value = false;
  }
}

async function loadBreakdown() {
  loadingBreakdown.value = true;
  try {
    const res = await linksApi.linkBreakdown(linkId.value, breakdownParams());
    breakdown.value = res.items || [];
  } finally {
    loadingBreakdown.value = false;
  }
}

async function loadClicks() {
  loadingClicks.value = true;
  try {
    const params: Parameters<typeof linksApi.linkClicks>[1] = {
      ...rangeParams(),
      page: page.value,
      page_size: pageSize,
    };
    if (filters.outcome) params.outcome = filters.outcome;
    if (filters.is_bot !== '') params.is_bot = Number(filters.is_bot);
    applyDimensionFilters(params, filters);
    if (filters.correlation_id.trim()) params.correlation_id = filters.correlation_id.trim();
    if (filters.aggregator_id != null) params.aggregator_id = filters.aggregator_id;
    const res = await linksApi.linkClicks(linkId.value, params);
    clicks.value = res.items || [];
    clicksTotal.value = res.total || 0;
  } finally {
    loadingClicks.value = false;
  }
}

function reloadClicks() {
  page.value = 1;
  loadClicks().catch(showError);
}

async function exportClicksCsv() {
  exportingCsv.value = true;
  try {
    const params: Parameters<typeof linksApi.linkClicksCsv>[1] = {
      ...rangeParams(),
      variant: 'basic',
    };
    if (filters.outcome) params.outcome = filters.outcome;
    if (filters.is_bot !== '') params.is_bot = Number(filters.is_bot);
    applyDimensionFilters(params, filters);
    if (filters.aggregator_id != null) params.aggregator_id = filters.aggregator_id;
    const code = link.value?.code || String(linkId.value);
    await linksApi.linkClicksCsv(linkId.value, params, `link-${code}-clicks.csv`);
    showSuccess('CSV скачан');
  } catch (e) {
    showError(e);
  } finally {
    exportingCsv.value = false;
  }
}

async function exportClicksCsvFull() {
  exportingCsv.value = true;
  try {
    const params: Parameters<typeof linksApi.linkClicksCsv>[1] = {
      ...rangeParams(),
      variant: 'full',
    };
    if (filters.outcome) params.outcome = filters.outcome;
    if (filters.is_bot !== '') params.is_bot = Number(filters.is_bot);
    applyDimensionFilters(params, filters);
    if (filters.aggregator_id != null) params.aggregator_id = filters.aggregator_id;
    const code = link.value?.code || String(linkId.value);
    await linksApi.linkClicksCsv(linkId.value, params, `link-${code}-clicks-full.csv`);
    showSuccess('CSV (full) скачан');
  } catch (e) {
    showError(e);
  } finally {
    exportingCsv.value = false;
  }
}

async function exportClicksXlsx() {
  exportingCsv.value = true;
  try {
    const params: Parameters<typeof linksApi.linkClicksXlsx>[1] = {
      ...rangeParams(),
      variant: 'full',
    };
    if (filters.outcome) params.outcome = filters.outcome;
    if (filters.is_bot !== '') params.is_bot = Number(filters.is_bot);
    applyDimensionFilters(params, filters);
    if (filters.aggregator_id != null) params.aggregator_id = filters.aggregator_id;
    const code = link.value?.code || String(linkId.value);
    await linksApi.linkClicksXlsx(linkId.value, params, `link-${code}-clicks.xlsx`);
    showSuccess('Excel скачан');
  } catch (e) {
    showError(e);
  } finally {
    exportingCsv.value = false;
  }
}

function onExportItemClick(e: { itemData?: { id?: ExportId } }) {
  const id = e.itemData?.id;
  if (id) selectedExportId.value = id;
}

function onExportButtonClick() {
  switch (selectedExportId.value) {
    case 'csv-full':
      void exportClicksCsvFull();
      break;
    case 'excel':
      void exportClicksXlsx();
      break;
    default:
      void exportClicksCsv();
  }
}

function prevClicksPage() {
  if (page.value > 1) {
    page.value -= 1;
    loadClicks().catch(showError);
  }
}

function nextClicksPage() {
  if (page.value * pageSize < clicksTotal.value) {
    page.value += 1;
    loadClicks().catch(showError);
  }
}

async function onRangeChange() {
  try {
    if (tabIndex.value === 0) await loadSummary();
    else if (tabIndex.value === 1) await loadBreakdown();
    else await reloadClicks();
  } catch (e) {
    showError(e);
  }
}

async function copyUrl() {
  try {
    await copyText(displayUrl.value);
    showSuccess('Скопировано');
  } catch {
    showError(new Error('Не удалось скопировать'));
  }
}

async function removeLink() {
  if (!link.value) return;
  if (!window.confirm(`Удалить ссылку ${link.value.code}? Это нельзя отменить.`)) return;
  deleting.value = true;
  try {
    await linksApi.deleteLink(linkId.value);
    showSuccess('Ссылка удалена');
    await router.push('/tracker/links');
  } catch (e) {
    showError(e);
    deleting.value = false;
  }
}

watch(tabIndex, async (t) => {
  try {
    if (t === 0) await loadSummary();
    else if (t === 1) await loadBreakdown();
    else await loadClicks();
  } catch (e) {
    showError(e);
  }
});

onMounted(async () => {
  void aggregatorsLookup.ensureLoaded();
  try {
    await loadLink();
    await loadSummary();
  } catch (e) {
    showError(e);
  }
});
</script>

<template>
  <BottoPagePanel title="" standalone>
    <template #header>
      <div class="header-meta">
        <a class="back" href="#" @click.prevent="router.push('/tracker/links')">← Ссылки</a>
        <h2 v-if="link" class="link-name">{{ linkTitle }}</h2>
        <p v-if="link" class="subtitle">
          <span class="mono">{{ link.code }}</span>
          ·
          <span class="mono">{{ link.domain_name }}</span>
          ·
          <span v-if="link.tracker_node_ip || link.tracker_node_name" class="mono">
            edge {{ link.tracker_node_ip || link.tracker_node_name }}
          </span>
          <template v-if="link.tracker_node_ip || link.tracker_node_name"> · </template>
          <StatusBadge :status="link.status" />
          · challenge {{ link.challenge_mode }}
        </p>
      </div>
    </template>
    <template #actions>
      <DxButton v-if="link" text="Copy URL" styling-mode="outlined" @click="copyUrl" />
      <DxButton
        v-if="link"
        text="Удалить"
        type="danger"
        styling-mode="outlined"
        :disabled="deleting"
        @click="removeLink"
      />
    </template>

    <div class="link-page">
    <div class="toolbar">
      <DateRangePicker v-model:from="rangeFrom" v-model:to="rangeTo" @change="onRangeChange" />
      <span class="toolbar__hint">Период для кликов, uniques и breakdown</span>
    </div>

    <DxTabPanel
      class="link-tabs"
      height="100%"
      v-model:selected-index="tabIndex"
      :defer-rendering="false"
    >
      <DxItem title="Overview">
        <div class="tab-body">
          <div v-if="summary" class="stat-cards">
            <div v-for="c in summaryCards" :key="c.label" class="stat-card">
              <div class="stat-card__label">{{ c.label }}</div>
              <div class="stat-card__value">{{ c.value }}</div>
            </div>
          </div>
          <div v-else class="empty">{{ loadingSummary ? 'Загрузка…' : 'Нет данных.' }}</div>

          <div v-if="link" class="details-card">
            <h3>Ссылка</h3>
            <div class="details-grid">
              <div class="muted">Имя</div>
              <div>{{ link.name || '—' }}</div>
              <div class="muted">Tracking URL</div>
              <div class="mono">{{ displayUrl }}</div>
              <div class="muted">Target</div>
              <div class="mono">{{ link.target_url }}</div>
              <div class="muted">Correlation ID</div>
              <div class="mono">{{ link.correlation_id || '—' }}</div>
              <div class="muted">Aggregator ID</div>
              <div class="mono">{{ link.aggregator_id || '—' }}</div>
              <div class="muted">Истекает</div>
              <div>{{ formatDateTime(link.expires_at) }}</div>
              <div class="muted">Создана</div>
              <div>{{ formatDateTime(link.created_at) }}</div>
            </div>
          </div>
        </div>
      </DxItem>

      <DxItem title="Breakdown">
        <div class="tab-body">
          <div class="toolbar">
            <div class="filter-field">
              <label>Group by</label>
              <DxSelectBox
                v-model:value="groupBy"
                :items="groupOptions"
                width="200"
                @value-changed="() => loadBreakdown().catch(showError)"
              />
            </div>
            <DimensionFilterFields
              v-model:country="breakdownFilters.country"
              v-model:language="breakdownFilters.language"
              v-model:device-type="breakdownFilters.device_type"
              :link-id="linkId"
              :from="rangeParams().from"
              :to="rangeParams().to"
              @change="() => loadBreakdown().catch(showError)"
            />
          </div>
          <DxDataGrid
            :data-source="breakdown"
            v-bind="trackerGridProps"
            :loading="loadingBreakdown"
            key-expr="key"
            height="100%"
            width="100%"
          >
            <DxScrolling mode="standard" />
            <DxColumn :caption="groupBy" :calculate-cell-value="(r: BreakdownRow) => r.key || '(empty)'" css-class="mono" />
            <DxColumn data-field="clicks" caption="Клики" width="100" />
            <DxColumn data-field="unique_clicks" caption="Uniques" width="100" />
            <DxColumn data-field="bots" caption="Bots" width="90" />
          </DxDataGrid>
        </div>
      </DxItem>

      <DxItem title="Click log">
        <div class="tab-body">
          <div class="toolbar filters">
            <div class="filter-field">
              <label>Outcome</label>
              <DxSelectBox
                v-model:value="filters.outcome"
                :items="outcomeItems"
                value-expr="value"
                display-expr="text"
                width="140"
                @value-changed="reloadClicks"
              />
            </div>
            <div class="filter-field">
              <label>Bot</label>
              <DxSelectBox
                v-model:value="filters.is_bot"
                :items="botItems"
                value-expr="value"
                display-expr="text"
                width="120"
                @value-changed="reloadClicks"
              />
            </div>
            <DimensionFilterFields
              v-model:country="filters.country"
              v-model:language="filters.language"
              v-model:device-type="filters.device_type"
              :link-id="linkId"
              :from="rangeParams().from"
              :to="rangeParams().to"
              @change="reloadClicks"
            />
            <div class="filter-field">
              <label>Correlation ID</label>
              <DxTextBox
                v-model:value="filters.correlation_id"
                placeholder="correlation…"
                width="180"
                @enter-key="reloadClicks"
              />
            </div>
            <div class="filter-field filter-field--lookup">
              <label>Агрегатор</label>
              <LookupBox
                v-model="filters.aggregator_id"
                :lookup="aggregatorsLookup"
                placeholder="Агрегатор"
                @update:model-value="reloadClicks"
              />
            </div>
            <DxButton text="Применить" styling-mode="outlined" @click="reloadClicks" />
            <DxDropDownButton
              :items="exportItems"
              key-expr="id"
              display-expr="text"
              :selected-item-key="selectedExportId"
              :split-button="true"
              :use-select-mode="true"
              styling-mode="outlined"
              icon="download"
              :text="exportButtonText"
              :disabled="exportingCsv || clicksTotal === 0"
              @button-click="onExportButtonClick"
              @item-click="onExportItemClick"
            />
          </div>

          <ClickLogGrid :clicks="clicks" :loading="loadingClicks" />
          <div v-if="clicksTotal > 0" class="pager">
            <DxButton text="Назад" styling-mode="outlined" :disabled="page <= 1" @click="prevClicksPage" />
            <span class="pager__info">Стр. {{ page }} · {{ formatNumber(clicksTotal) }} всего</span>
            <DxButton
              text="Вперёд"
              styling-mode="outlined"
              :disabled="page * pageSize >= clicksTotal"
              @click="nextClicksPage"
            />
          </div>
        </div>
      </DxItem>
    </DxTabPanel>
    </div>
  </BottoPagePanel>
</template>

<style scoped>
.link-page {
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 0;
  overflow: hidden;
  gap: 12px;
}

.header-meta {
  flex: 1;
  min-width: 0;
}

.back {
  display: inline-block;
  margin-bottom: 4px;
  font-size: 0.875rem;
  color: var(--dx-color-text-secondary, #666);
  text-decoration: none;
}

.back:hover {
  text-decoration: underline;
}

.link-name {
  margin: 0 0 4px;
  font-size: 1.25rem;
  font-weight: 600;
  line-height: 1.3;
  word-break: break-word;
}

.subtitle {
  margin: 0;
  font-size: 0.875rem;
  color: var(--dx-color-text-secondary, #666);
}

.toolbar {
  flex: 0 0 auto;
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  align-items: flex-end;
}

.toolbar__hint {
  font-size: 12px;
  color: var(--dx-color-text-secondary, #666);
  padding-bottom: 8px;
}

.filter-field {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.filter-field label {
  font-size: 12px;
  color: var(--dx-color-text-secondary, #666);
}

.filter-field--lookup {
  min-width: 220px;
}

.link-tabs {
  flex: 1 1 auto;
  min-height: 0;
}

.link-tabs :deep(.dx-tabpanel-container),
.link-tabs :deep(.dx-multiview-wrapper),
.link-tabs :deep(.dx-multiview-item-container),
.link-tabs :deep(.dx-multiview-item),
.link-tabs :deep(.dx-multiview-item-content) {
  height: 100% !important;
  min-height: 0;
}

.tab-body {
  height: 100%;
  min-height: 0;
  padding-top: 8px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  gap: 12px;
}

.tab-body > :deep(.dx-datagrid),
.tab-body > :deep(.click-log-grid) {
  flex: 1 1 auto;
  min-height: 0;
  width: 100%;
}

.stat-cards {
  flex: 0 0 auto;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
  gap: 12px;
}

.stat-card {
  padding: 12px 14px;
  border: 1px solid var(--dx-color-border, #e0e0e0);
  border-radius: 6px;
  background: var(--dx-component-color-bg, #fff);
}

.stat-card__label {
  font-size: 12px;
  color: var(--dx-color-text-secondary, #666);
  margin-bottom: 4px;
}

.stat-card__value {
  font-size: 1.25rem;
  font-weight: 600;
}

.details-card {
  flex: 0 0 auto;
  padding: 14px;
  border: 1px solid var(--dx-color-border, #e0e0e0);
  border-radius: 6px;
}

.details-card h3 {
  margin: 0 0 10px;
  font-size: 1rem;
}

.details-grid {
  display: grid;
  grid-template-columns: 140px 1fr;
  gap: 6px 12px;
  font-size: 0.875rem;
}

.detail-expand {
  padding: 8px 12px;
}

.empty {
  color: var(--dx-color-text-secondary, #666);
  font-size: 0.875rem;
}

.pager {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  gap: 12px;
}

.pager__info {
  font-size: 0.875rem;
  color: var(--dx-color-text-secondary, #666);
}

.muted {
  color: var(--dx-color-text-secondary, #666);
}

:deep(.mono) {
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: 0.9em;
}
</style>
